import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  Divider,
  Grid,
  Chip,
  IconButton,
  TextField,
  Avatar,
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from "@mui/lab";
import {
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Map as MapIcon,
  LocalShipping as DispatchIcon,
  CheckCircle as CloseIcon,
  Send as SendIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Assignment as AssignmentIcon,
} from "@mui/icons-material";
import { SightingTypeBadge, SightingStatusBadge, SightingSourceBadge } from "../components";
import { getSightingById } from "../data/sightingsData";
import { CreateWorkOrderDialog } from "../../workorders/components/CreateWorkOrderDialog";
import { format, formatDistanceToNow } from "date-fns";

export const SightingDetail = () => {
  const { sightingId } = useParams<{ sightingId: string }>();
  const navigate = useNavigate();
  const sighting = getSightingById(sightingId || "");
  const [woDialogOpen, setWoDialogOpen] = useState(false);

  if (!sighting) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h5" color="text.secondary">
          Sighting not found
        </Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/sightings")} sx={{ mt: 2 }}>
          Back to Sightings
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header with actions */}
      <Card variant="outlined" sx={{ mb: 2, flexShrink: 0 }}>
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
            <IconButton onClick={() => navigate("/sightings")} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontFamily: "monospace" }}>{sighting.id}</Typography>
            <SightingTypeBadge type={sighting.type} />
            <SightingStatusBadge status={sighting.status} />
            <SightingSourceBadge source={sighting.source} />
            <Chip label={sighting.zone} size="small" variant="outlined" />
            <Typography variant="body2" color="text.secondary">
              {formatDistanceToNow(new Date(sighting.dateReported), { addSuffix: true })}
            </Typography>

            <Box sx={{ flex: 1 }} />

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button size="small" variant="outlined" startIcon={<MapIcon />} onClick={() => navigate("/geospatial")}>
                Map
              </Button>
              <Button size="small" variant="outlined" startIcon={<PhoneIcon />}>
                Call Tech
              </Button>
              <Button size="small" variant="outlined" startIcon={<EmailIcon />}>
                Email
              </Button>
              <Button size="small" variant="outlined" startIcon={<AssignmentIcon />} onClick={() => setWoDialogOpen(true)}>
                Work Order
              </Button>
              {sighting.status === "open" && (
                <Button size="small" variant="contained" startIcon={<DispatchIcon />}>
                  Dispatch
                </Button>
              )}
              {sighting.status === "dispatched" && (
                <Button size="small" variant="contained" color="success" startIcon={<CloseIcon />}>
                  Close
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Main content */}
      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        {/* Left Column */}
        <Grid size={{ xs: 12, lg: 8 }} sx={{ height: "100%" }}>
          <Box sx={{ height: "100%", overflow: "auto", pr: 1 }}>
            <Stack spacing={2}>
              {/* Sighting Details Card */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Reference</Typography>
                  <Typography variant="h6" sx={{ fontFamily: "monospace", mb: 2 }}>{sighting.referenceNumber}</Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {sighting.description}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Location</Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2">{sighting.location}</Typography>
                      </Stack>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Map Reference</Typography>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                        {sighting.mapReference}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Coordinates</Typography>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                        {sighting.coordinates[1].toFixed(4)}, {sighting.coordinates[0].toFixed(4)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Zone</Typography>
                      <Chip label={sighting.zone} size="small" variant="outlined" />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Reported</Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <ScheduleIcon fontSize="small" color="action" />
                        <Typography variant="body2">{format(new Date(sighting.dateReported), "PP p")}</Typography>
                      </Stack>
                    </Grid>
                    {sighting.dispatchedAt && (
                      <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">Dispatched</Typography>
                        <Typography variant="body2">{format(new Date(sighting.dispatchedAt), "PP p")}</Typography>
                      </Grid>
                    )}
                    {sighting.closedAt && (
                      <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">Closed</Typography>
                        <Typography variant="body2">{format(new Date(sighting.closedAt), "PP p")}</Typography>
                      </Grid>
                    )}
                    {sighting.sourceId && (
                      <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">Source ID</Typography>
                        <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                          {sighting.sourceId}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {/* Assignment Card */}
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Stack direction="row" alignItems="center" spacing={3} flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle2" sx={{ minWidth: 80 }}>Assignment</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar sx={{ width: 28, height: 28, bgcolor: "primary.main" }}>
                        <PersonIcon sx={{ fontSize: 16 }} />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Technician</Typography>
                        <Typography variant="body2">{sighting.technician || "Unassigned"}</Typography>
                      </Box>
                    </Stack>
                    <Chip
                      label={sighting.techAware ? "Tech Aware" : "Not Notified"}
                      size="small"
                      color={sighting.techAware ? "success" : "default"}
                      variant="outlined"
                    />
                    <Box sx={{ flex: 1 }} />
                    <Button size="small" variant="outlined">Reassign</Button>
                  </Stack>
                </CardContent>
              </Card>

              {/* Activity Timeline */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>Activity Timeline</Typography>
                  {sighting.actions && sighting.actions.length > 0 ? (
                    <Timeline sx={{ p: 0, m: 0 }}>
                      {sighting.actions.map((action, index) => (
                        <TimelineItem key={action.id} sx={{ "&::before": { display: "none" }, minHeight: 48 }}>
                          <TimelineSeparator>
                            <TimelineDot
                              sx={{ m: 0 }}
                              color={
                                action.type === "close" ? "success" :
                                action.type === "dispatch" ? "warning" :
                                action.type === "tech_aware" ? "info" : "grey"
                              }
                              variant={index === 0 ? "filled" : "outlined"}
                            />
                            {index < sighting.actions!.length - 1 && <TimelineConnector />}
                          </TimelineSeparator>
                          <TimelineContent sx={{ py: 0.5, px: 1.5 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                              <Box>
                                <Typography variant="body2">{action.description}</Typography>
                                <Typography variant="caption" color="text.secondary">by {action.actor}</Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {formatDistanceToNow(new Date(action.timestamp), { addSuffix: true })}
                              </Typography>
                            </Stack>
                          </TimelineContent>
                        </TimelineItem>
                      ))}
                    </Timeline>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No activity recorded yet</Typography>
                  )}
                </CardContent>
              </Card>
            </Stack>
          </Box>
        </Grid>

        {/* Right Column - Notes */}
        <Grid size={{ xs: 12, lg: 4 }} sx={{ height: "100%" }}>
          <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ px: 2, pt: 2, pb: 1, flexShrink: 0 }}>
              <Typography variant="subtitle2">Communication Log</Typography>
            </Box>

            <Box sx={{ flex: 1, overflow: "auto", px: 2 }}>
              {sighting.notes && sighting.notes.length > 0 ? (
                <Stack spacing={2}>
                  {sighting.notes.map((note) => (
                    <Box key={note.id} sx={{ display: "flex", gap: 1.5 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: 12, flexShrink: 0 }}>
                        {note.author.charAt(0)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography variant="body2" fontWeight={500}>{note.author}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDistanceToNow(new Date(note.timestamp), { addSuffix: true })}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {note.content}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">No communications yet</Typography>
              )}
            </Box>

            <Box sx={{ p: 2, borderTop: 1, borderColor: "divider", flexShrink: 0 }}>
              <Stack direction="row" spacing={1}>
                <TextField placeholder="Add a note..." size="small" fullWidth multiline maxRows={3} />
                <Button variant="contained" size="small" sx={{ minWidth: "auto", px: 1.5 }}>
                  <SendIcon fontSize="small" />
                </Button>
              </Stack>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <CreateWorkOrderDialog
        open={woDialogOpen}
        onClose={() => setWoDialogOpen(false)}
        fromSighting={sighting}
      />
    </Box>
  );
};
